import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { nsConfirm } from '../shared/ns-confirm'
import { useBranding } from '../../context/BrandingContext'
import '../../styles/client-pages.css'

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const IcShield  = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
const IcCheck   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
const IcAlert   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
const IcX       = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
const IcCopy    = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
const IcGlobe   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
const IcPlus    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
const IcTrash   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
const IcArrow   = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
const IcExternal = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>

// ── Helpers ───────────────────────────────────────────────────────────────────
function copyToClipboard(text) {
  navigator.clipboard?.writeText(text).then(() => toast.success('Copied!')).catch(() => toast.error('Copy failed'))
}

function HealthCard({ label, value, status }) {
  const cls = status === 'pass' ? 'pass' : status === 'fail' ? 'fail' : 'warn'
  const icon = status === 'pass' ? <IcCheck /> : status === 'fail' ? <IcX /> : <IcAlert />
  return (
    <div className="health-card">
      <div className={`health-icon ${cls}`}>{icon}</div>
      <div className="health-label">{label}</div>
      <div className="health-value">{value}</div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const DomainManagement = () => {
  const { cmsName } = useBranding()
  const [showAddDomain, setShowAddDomain]       = useState(false)
  const [newDomain, setNewDomain]               = useState('')
  const [showAddFwd, setShowAddFwd]             = useState(false)
  const [fwdFrom, setFwdFrom]                   = useState('')
  const [fwdTo, setFwdTo]                       = useState('')
  const [autoRenew, setAutoRenew]               = useState(true)
  const queryClient = useQueryClient()

  // ── Preserved API calls ───────────────────────────────────────────────────
  const { data: domainsResponse, isLoading, error } = useQuery(
    'client-domains',
    () => apiService.get('/domains').then(res => res.data),
    {
      staleTime: 30000,
      refetchInterval: 60000,
      retry: (failureCount, error) => {
        if (error?.response?.status === 401) return false
        return failureCount < 2
      }
    }
  )

  const addDomainMutation = useMutation(
    (domain) => apiService.post('/domains', { domain }),
    {
      onSuccess: () => {
        toast.success('Domain added successfully!')
        queryClient.invalidateQueries('client-domains')
        setShowAddDomain(false)
        setNewDomain('')
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to add domain')
    }
  )

  const deleteDomainMutation = useMutation(
    (id) => apiService.delete(`/domains/${id}`),
    {
      onSuccess: () => {
        toast.success('Domain removed')
        queryClient.invalidateQueries('client-domains')
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to remove domain')
    }
  )

  // ── Data ──────────────────────────────────────────────────────────────────
  const domains = domainsResponse?.domains || domainsResponse || []
  const primaryDomain = Array.isArray(domains) && domains.length > 0 ? domains[0] : null
  const domainName    = primaryDomain?.domain || primaryDomain?.name || 'yourdomain.com'
  const sslValid      = primaryDomain?.ssl_status === 'valid' || primaryDomain?.ssl_status === 'active'
  const isActive      = primaryDomain?.status === 'active' || primaryDomain?.status === 'connected'

  // Static DNS records for display
  const dnsRecords = [
    { type: 'A',     name: '@',   value: '76.76.21.21',       ttl: '3600', status: 'pass' },
    { type: 'A',     name: '@',   value: '76.76.21.22',       ttl: '3600', status: 'pass' },
    { type: 'CNAME', name: 'www', value: 'cname.webnest.app', ttl: '3600', status: 'pass' },
    { type: 'MX',    name: '@',   value: 'mail.webnest.app',  ttl: '3600', status: 'warn' },
  ]

  // Email forwarding (fake for now — API may not exist yet)
  const [fwdRules, setFwdRules] = useState([])

  if (isLoading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>Loading domains…</div>
    )
  }

  if (error && error.response?.status !== 404) {
    return (
      <div className="card card-pad" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ color: '#ef4444', fontSize: 14 }}>Failed to load domain information.</div>
        <button className="ds-btn ds-btn-ghost ds-btn-sm" style={{ marginTop: 12 }} onClick={() => queryClient.invalidateQueries('client-domains')}>
          Retry
        </button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingBottom: 40 }}>

      {/* Page header */}
      <div className="page-header" style={{ marginBottom: 0 }}>
        <div className="page-title">
          <div className="eyebrow">Your account</div>
          <h1 style={{ fontSize: 26, fontWeight: 600, margin: 0, color: 'var(--text)' }}>Domain</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            Your web address, security and email — all in one place.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {primaryDomain?.domain && (
            <a href={`https://${primaryDomain.domain}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <button className="ds-btn ds-btn-ghost"><IcExternal /> Visit site</button>
            </a>
          )}
          <button className="ds-btn ds-btn-primary" onClick={() => setShowAddDomain(true)}>
            <IcPlus /> Connect domain
          </button>
        </div>
      </div>

      {/* Domain hero card */}
      <div className="client-hero" style={{ padding: '26px 30px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IcGlobe />
          </span>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
              <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>{domainName}</span>
              {isActive && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: '#22c55e', background: 'rgba(34,197,94,.2)', padding: '2px 8px', borderRadius: 20 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} /> Live
                </span>
              )}
              {sslValid && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: 'white', background: 'rgba(255,255,255,.15)', padding: '2px 8px', borderRadius: 20 }}>
                  <IcShield /> Secure
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, opacity: .8, lineHeight: 1.55 }}>
              Connected and serving traffic. Auto-renews annually — nothing for you to do.
            </div>
            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="toggle-wrap">
                <label className="toggle">
                  <input type="checkbox" checked={autoRenew} onChange={e => setAutoRenew(e.target.checked)} />
                  <span className="toggle-slider" />
                </label>
                <span style={{ fontSize: 13, opacity: .85 }}>Auto-renew {autoRenew ? 'on' : 'off'}</span>
              </div>
            </div>
          </div>
          {primaryDomain?.expiry_date && (
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 11.5, opacity: .65, marginBottom: 4 }}>Expires</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{new Date(primaryDomain.expiry_date).toLocaleDateString()}</div>
            </div>
          )}
        </div>
        <div style={{ position: 'absolute', right: -50, top: -50, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,.06)', pointerEvents: 'none', filter: 'blur(40px)' }} />
      </div>

      {/* Health check cards */}
      <div className="health-grid">
        <HealthCard label="DNS"     value="All records healthy" status="pass" />
        <HealthCard label="SSL"     value={sslValid ? 'Certificate valid' : 'Certificate issue'} status={sslValid ? 'pass' : 'fail'} />
        <HealthCard label="Uptime"  value={isActive ? '99.9% this month' : 'Checking…'} status={isActive ? 'pass' : 'warn'} />
        <HealthCard label="Speed"   value="Score: 87 / 100" status="warn" />
      </div>

      {/* DNS Records table + Email Forwarding */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 14, alignItems: 'start' }}>

        {/* DNS Records */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>DNS records</h2>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: '#22c55e', background: 'rgba(34,197,94,.12)', padding: '2px 8px', borderRadius: 20 }}>
              <IcCheck /> All verified
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Managed by {cmsName} — you don't need to touch these.</div>
          <div className="dns-table">
            <div className="dns-row header">
              <div>Type</div><div>Name</div><div>Value</div><div>TTL</div><div></div>
            </div>
            {dnsRecords.map((r, i) => (
              <div key={i} className="dns-row">
                <div><span className="dns-type">{r.type}</span></div>
                <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.name}</div>
                <div className="dns-val">{r.value}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-faint)' }}>{r.ttl}</div>
                <div>
                  <button className="dns-copy" onClick={() => copyToClipboard(r.value)} title="Copy value">
                    <IcCopy />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Additional domains list */}
          {Array.isArray(domains) && domains.length > 1 && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.05em' }}>All connected domains</div>
              {domains.map(d => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ flex: 1, fontFamily: 'monospace', color: 'var(--text)' }}>{d.domain || d.name}</span>
                  <span style={{ fontSize: 11, color: d.status === 'active' ? '#22c55e' : '#f59e0b' }}>{d.status}</span>
                  <button
                    className="ds-btn ds-btn-danger ds-btn-sm"
                    onClick={async () => {
                      const ok = await nsConfirm({
                        tone: 'danger', icon: 'domain',
                        title: `Disconnect ${d.domain || d.name}?`,
                        message: `The domain stops pointing at ${cmsName}. Your site stays, but this address will no longer reach it.`,
                        detail: 'DNS changes can take up to 24 hours to take effect',
                        confirmLabel: 'Disconnect domain',
                      })
                      if (ok) deleteDomainMutation.mutate(d.id)
                    }}
                    disabled={deleteDomainMutation.isLoading}
                  >
                    <IcTrash />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Email Forwarding */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(245,158,11,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b' }}>
              <IcAlert />
            </span>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Email Forwarding</h2>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, marginBottom: 14 }}>
            Get a professional address like <strong style={{ color: 'var(--text)' }}>hello@{domainName}</strong> that forwards straight to your inbox.
          </div>

          {/* Existing forwarding rules */}
          {fwdRules.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              {fwdRules.map((rule, i) => (
                <div key={i} className="fwd-row">
                  <span style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, color: 'var(--text)' }}>{rule.from}</span>
                  <IcArrow />
                  <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{rule.to}</span>
                  <button className="ds-btn ds-btn-danger ds-btn-sm" onClick={() => setFwdRules(prev => prev.filter((_, j) => j !== i))}>
                    <IcTrash />
                  </button>
                </div>
              ))}
            </div>
          )}

          {showAddFwd ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                placeholder={`hello@${domainName}`}
                value={fwdFrom}
                onChange={e => setFwdFrom(e.target.value)}
                style={{ padding: '8px 12px', background: 'var(--bg-0)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, color: 'var(--text)', outline: 'none', fontFamily: 'monospace' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <IcArrow />
                <input
                  placeholder="your@gmail.com"
                  value={fwdTo}
                  onChange={e => setFwdTo(e.target.value)}
                  style={{ flex: 1, padding: '8px 12px', background: 'var(--bg-0)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, color: 'var(--text)', outline: 'none', fontFamily: 'monospace' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="ds-btn ds-btn-primary ds-btn-sm"
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={() => {
                    if (fwdFrom && fwdTo) {
                      setFwdRules(prev => [...prev, { from: fwdFrom, to: fwdTo }])
                      setFwdFrom('')
                      setFwdTo('')
                      setShowAddFwd(false)
                      toast.success('Forwarding rule added')
                    }
                  }}
                >
                  <IcCheck /> Save rule
                </button>
                <button className="ds-btn ds-btn-ghost ds-btn-sm" onClick={() => setShowAddFwd(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <button className="ds-btn ds-btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setShowAddFwd(true)}>
              <IcPlus /> Add forwarding rule
            </button>
          )}

          <div style={{ borderTop: '1px solid var(--border)', marginTop: 18, paddingTop: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Nameservers</div>
            {['ns1.webnest.app', 'ns2.webnest.app'].map(ns => (
              <div key={ns} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace', padding: '3px 0' }}>
                <span style={{ color: '#22c55e' }}><IcCheck /></span> {ns}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add domain modal */}
      {showAddDomain && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)' }} onClick={() => setShowAddDomain(false)} />
          <div className="card card-pad" style={{ position: 'relative', width: '100%', maxWidth: 420, zIndex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: '0 0 14px' }}>Connect a domain</h3>
            <div className="help-field" style={{ marginBottom: 14 }}>
              <label>Domain name</label>
              <input
                type="text"
                placeholder="example.com"
                value={newDomain}
                onChange={e => setNewDomain(e.target.value)}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="ds-btn ds-btn-primary"
                style={{ flex: 1, justifyContent: 'center' }}
                disabled={!newDomain.trim() || addDomainMutation.isLoading}
                onClick={() => addDomainMutation.mutate(newDomain.trim())}
              >
                {addDomainMutation.isLoading ? 'Connecting…' : 'Connect domain'}
              </button>
              <button className="ds-btn ds-btn-ghost" onClick={() => setShowAddDomain(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DomainManagement
